import { CheckCircle2 } from "lucide-react";
import StatsCounter from "./StatsCounter/StatsCounter";
import { LazyLoader } from "~/lib/utils/LazyLoader";
import { Link } from "@remix-run/react";
import Container from "~/components/atoms/Container";
import { getFormattedCountryName } from "~/lib/helpers/constants";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import { useState } from "react";
import { UniqueVisitorModal } from "../Modals/UniqueVisitorModal/UniqueVisitorModal";
import { HOME_PAGE_SECTION_IDS } from "./constants";

export default function WhyOfficeHub({ region, searchUrl, isMobile }: { region: string, country: string, searchUrl?: string, isMobile: boolean }) {
  const [uniqueVisitorModal, setUniqueVisitorModal] = useState(false);
  const features = [
    {
      title: "Transparent and Verified Listings",
      description:
        `Every workspace in ${getFormattedCountryName(region)} is checked, verified, and updated with accurate pricing. What you see online is what you’ll experience in person.`,
    },
    {
      title: "Local Experts, Real Support",
      description:
        `Our ${getFormattedCountryName(region)}-based workspace specialists will shortlist the best options, arrange tours, and negotiate great deals on your behalf.`,
    },
    {
      title: "All-in-One Marketplace",
      description:
        `Explore coworking, serviced, private, and enterprise offices across ${getFormattedCountryName(region)} — all in one place.`,
    },
    {
      title: "Free and Hassle-Free",
      description:
        `Our help is 100% free. No hidden fees, no pressure, just honest advice and local expertise to find your ideal workspace.`,
    },
  ];

  return (
    <section id={HOME_PAGE_SECTION_IDS.whyOfficeHub} className="bg-white py-4 md:py-16 scroll-mt-28">
      <Container htmlFor="ultra-wide">
        <div className="mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Map with Image Overlay */}
          <div className="relative hidden md:flex md:justify-center md:items-center">
            <img
              src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/why-office-hub.avif`}
              alt="Office Hub search map with a selected location pop-out, showcasing real-time availability in top international business hubs"
              title="Office Rental Map"
              width={600}
              height={400}
              className="rounded-lg"
              loading="lazy"
            />

          </div>

          {/* Right Side - Content */}
          <div>
            <h2 className="text-base font-semibold text-center md:text-left text-base-red uppercase tracking-wide mb-2">
              Why Choose Office Hub?
            </h2>
            <p className="text-3xl md:text-5xl font-extrabold text-gray-800 mb-6 leading-tight text-center md:text-left ">
              The World’s Leading Marketplace for Flexible Office Spaces.
            </p>

            <div className="space-y-6">
              {features.map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 fill="#12AFBD" className="w-7 h-7 text-white flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <LazyLoader>
          <StatsCounter isMobile={isMobile} />

        </LazyLoader>
        {searchUrl && (
          <div className='flex justify-center mt-4 md:mt-8 px-4'>
            <Link to={searchUrl} target="_blank" prefetch="intent" className="w-full md:w-auto">
              <OhButton className='px-16 w-full'>
                Explore Offices Nearby
              </OhButton>
            </Link>
          </div>
        )}
        {!searchUrl && (
          <div className='flex justify-center mt-8 px-4 '>
            <OhButton onClick={() => setUniqueVisitorModal(true)} className='px-16 w-full md:w-auto'>
              Contact Us
            </OhButton>
          </div>
        )}
        {
          uniqueVisitorModal && (
            <UniqueVisitorModal visible={uniqueVisitorModal} params={null} handleCancel={() => setUniqueVisitorModal(false)} handleSubmit={() => setUniqueVisitorModal(false)} region={region} />
          )
        }
      </Container>
    </section>
  );
}
